import { useCallback, useMemo } from 'react';

import { useFormListValidation } from 'core';
import { User, useEditUserImage, useEditUserInfo } from 'core/Api';
import { getErrorMessage_v2 } from 'core/Utils';
import { ButtonMantine, notifications } from 'ui';

import { EditProfileForms } from './editProfile.types';

type UserRequestType = { user: Partial<User> };

const SaveProfile = ({ onClose }: { onClose: () => void }) => {
  const { useGetForm } = useFormListValidation<EditProfileForms>();
  const { mutateAsync: updateUserInfo, isPending: isLoadingEditUserInfo } = useEditUserInfo();
  const { mutateAsync: updateUserImage, isPending: isLoadingEditUserImage } = useEditUserImage();

  const profileImageForm = useGetForm('profileImageForm');
  const handleUserImageUpdate = useCallback(async () => {
    if (profileImageForm?.values?.file) {
      try {
        await updateUserImage(profileImageForm.values.file);
      } catch (error) {
        notifications.show({
          message: 'Error updating user image',
          variant: 'error',
          autoClose: false,
        });
      }
    }
  }, [updateUserImage, profileImageForm?.values?.file]);

  const profileForm = useGetForm('profileForm');
  const saveProfileAndOrImage = useCallback(async () => {
    if (profileForm?.isValid()) {
      const {
        firstName = '',
        lastName = '',
        phoneNumber = '',
        address1 = '',
        address2 = '',
        city = '',
        state = '',
        zipCode = '',
        country = '',
        mobileCarrier,
      } = profileForm.values;
      const userInfo: UserRequestType = {
        user: {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          phone_number: phoneNumber.trim(),
          city: city.trim(),
          state,
          address1: address1.trim(),
          address2: address2.trim(),
          zip_code: zipCode.trim(),
          country: country.trim(),
          phone_number_provider: mobileCarrier ? mobileCarrier : undefined,
        },
      };

      // If the user info is changed, make a request to update it
      if (profileForm.isDirty()) {
        try {
          await updateUserInfo(userInfo);
        } catch (error) {
          notifications.show({
            message: `Error updating user info: ${getErrorMessage_v2(error)}`,
            variant: 'error',
          });

          // If it fails, just back out since the user doesn't know that there are two requests
          return;
        }
      }

      await handleUserImageUpdate();

      notifications.show({
        message: 'Successfully updated profile',
        variant: 'success',
      });

      onClose();
    }
  }, [profileForm, handleUserImageUpdate, onClose, updateUserInfo]);

  const disabled = useMemo(() => {
    return (
      isLoadingEditUserInfo ||
      isLoadingEditUserImage ||
      (!profileForm?.isDirty() && !profileImageForm?.values?.file)
    );
  }, [isLoadingEditUserInfo, isLoadingEditUserImage, profileForm, profileImageForm?.values?.file]);

  return (
    <ButtonMantine fullWidth onClick={saveProfileAndOrImage} disabled={disabled}>
      Save Changes
    </ButtonMantine>
  );
};

export { SaveProfile as SaveProfileButton };
